import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useEnabledModules } from "@/lib/permissions";
import {
  FolderKanban,
  Briefcase,
  ListChecks,
  AlertOctagon,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import Pill from "@/components/Pill";

const KPI_CARDS = [
  { key: "projects", label: "CDARK", icon: FolderKanban, link: "/projects", extraKey: "open_tickets", extraLabel: "open tickets" },
  { key: "biz_projects", label: "Projects (Open)", icon: Briefcase, link: "/biz-projects", overdueKey: "biz_projects" },
  { key: "tasks", label: "Tasks", icon: ListChecks, link: "/tasks", overdueKey: "tasks" },
  { key: "issues", label: "Tickets", icon: AlertOctagon, link: "/issues", overdueKey: "tickets" },
];

const PRI_COLORS = {
  Critical: "#ef4444",
  High: "#f97316",
  Medium: "#f59e0b",
  Low: "#10b981",
  Unknown: "#52525b",
};

function StatCard({ name, label, value, icon: Icon, link, overdue, extra, extraLabel }) {
  const card = (
    <div
      className="bg-zinc-900 border border-white/10 rounded-lg p-5 hover:-translate-y-0.5 hover:border-white/20 transition-all duration-200 h-full"
      data-testid={`stat-${name}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="label-overline">{label || name}</div>
        <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center text-zinc-300">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="kpi-number text-3xl text-white">{value}</div>
      </div>
      {extra !== undefined && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-400">
          <span className="font-mono font-medium text-white">{extra}</span>
          <span>{extraLabel}</span>
        </div>
      )}
      {overdue !== undefined && (
        <div className={`mt-3 inline-flex items-center gap-1.5 text-xs ${overdue > 0 ? "text-red-400" : "text-zinc-500"}`}>
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>
            <span className="font-mono font-medium">{overdue}</span> overdue
          </span>
        </div>
      )}
    </div>
  );
  return link ? <Link to={link} className="block">{card}</Link> : card;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const moduleMeta = useEnabledModules();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !stats) {
    return <div className="text-zinc-500 text-sm" data-testid="dashboard-loading">Loading dashboard…</div>;
  }

  // Per-widget visibility — admin always sees everything; non-admins need the
  // matching sub-permission (with a generous default ON if loading).
  const isAdmin = moduleMeta?.role === "admin";
  const mods = new Set(moduleMeta?.modules || []);
  const showKpis = isAdmin || moduleMeta == null || mods.has("dashboard_kpis");
  const showCharts = isAdmin || moduleMeta == null || mods.has("dashboard_charts");
  const showRecent = isAdmin || moduleMeta == null || mods.has("dashboard_recent");

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>
            Digi-One Dashboard
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            A live snapshot of CDARK, assets, services, tasks and tickets.
          </p>
        </div>
      </header>

      {/* KPI Grid */}
      {showKpis && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-widget-kpis">
        {KPI_CARDS.map((k) => (
          <StatCard
            key={k.key}
            name={k.key}
            label={k.label}
            value={stats.counts[k.key] ?? 0}
            icon={k.icon}
            link={k.link}
            overdue={k.overdueKey ? (stats.overdue?.[k.overdueKey] ?? 0) : undefined}
            extra={k.extraKey ? (stats.extras?.[k.extraKey] ?? 0) : undefined}
            extraLabel={k.extraLabel}
          />
        ))}
      </div>
      )}

      {/* Charts */}
      {showCharts && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dashboard-widget-charts">
        <ChartCard title="Tasks by priority" testid="chart-task-priority">
          <BarBlock data={stats.task_priority} />
        </ChartCard>
        <ChartCard title="Tickets by priority" testid="chart-issue-priority">
          <BarBlock data={stats.issue_priority} />
        </ChartCard>
        <ChartCard title="Tasks by status" testid="chart-task-status">
          <PieBlock data={stats.task_status} />
        </ChartCard>
        <ChartCard title="Tickets by status" testid="chart-issue-status">
          <PieBlock data={stats.issue_status} />
        </ChartCard>
      </div>
      )}

      {/* Recent */}
      {showRecent && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dashboard-widget-recent">
        <RecentList
          title="Recent CDARK"
          items={stats.recent_projects}
          render={(p) => (
            <>
              <div className="font-medium text-sm text-white truncate">{p.name}</div>
              <div className="text-xs text-zinc-500 font-mono">{p.code}</div>
            </>
          )}
          right={() => null}
          testid="recent-projects"
        />
        <RecentList
          title="Recent tasks"
          items={stats.recent_tasks}
          render={(t) => (
            <>
              <div className="font-medium text-sm text-white truncate">{t.subject}</div>
              <div className="text-xs text-zinc-500 font-mono">{t.code}</div>
            </>
          )}
          right={(t) => <Pill kind="status" value={t.status} />}
          testid="recent-tasks"
        />
        <RecentList
          title="Recent tickets"
          items={stats.recent_issues}
          render={(i) => (
            <>
              <div className="font-medium text-sm text-white truncate">{i.subject}</div>
              <div className="text-xs text-zinc-500 font-mono">{i.code}</div>
            </>
          )}
          right={(i) => <Pill kind="priority" value={i.priority} />}
          testid="recent-issues"
        />
      </div>
      )}
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid={testid}>
      <div className="label-overline mb-4">{title}</div>
      <div className="h-56">{children}</div>
    </div>
  );
}

function BarBlock({ data }) {
  if (!data || data.length === 0) {
    return <EmptyChart />;
  }
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ left: -10, right: 8, top: 8 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis dataKey="name" stroke="#71717a" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis stroke="#71717a" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip
          contentStyle={{
            background: "#09090b",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 6,
            fontSize: 12,
          }}
          cursor={{ fill: "rgba(255,255,255,0.04)" }}
        />
        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
          {data.map((entry, idx) => (
            <Cell key={idx} fill={PRI_COLORS[entry.name] || "#3f3f46"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function PieBlock({ data }) {
  if (!data || data.length === 0) return <EmptyChart />;
  const colors = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#52525b"];
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie data={data} dataKey="count" nameKey="name" innerRadius={48} outerRadius={75} paddingAngle={2}>
          {data.map((entry, idx) => (
            <Cell key={idx} fill={colors[idx % colors.length]} />
          ))}
        </Pie>
        <Legend
          wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }}
          iconType="circle"
        />
        <Tooltip
          contentStyle={{
            background: "#09090b",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 6,
            fontSize: 12,
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function EmptyChart() {
  return (
    <div className="h-full flex items-center justify-center text-xs text-zinc-600">
      No data yet
    </div>
  );
}

function RecentList({ title, items, render, right, testid }) {
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid={testid}>
      <div className="label-overline mb-3">{title}</div>
      {!items || items.length === 0 ? (
        <div className="text-xs text-zinc-600 py-6 text-center">Nothing here yet.</div>
      ) : (
        <ul className="divide-y divide-white/5">
          {items.map((it) => (
            <li key={it.id} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">{render(it)}</div>
              <div className="shrink-0">{right(it)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
